import React from 'react';
import { ArrowRight, Leaf, Users, BookOpen, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const TastingTomorrowPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_institute-clone/artifacts/vroc0nae_Tasting%20Tomorrow.png)',
            backgroundPosition: 'center 65%'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Tasting Tomorrow
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Cultural Heritage and Climate Change Through Food
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              How can cultural heritage adapt to climate change through food and cuisine?
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Highland Institute researchers Tümüzo Katiry, Akumtong Imchen, and Saktum Wonti, for Tasting Tomorrow, 
                investigated this question through an applied research methodology, namely preparing popular Naga dishes, 
                but instead of adding all the usual ingredients, they exchanged some for food items that were likely to be 
                available after 50 years of climate change.
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                The project explores how traditional foodways and culinary practices might evolve in response to climate-driven 
                changes in ingredient availability, while maintaining cultural identity and nutritional value. By cooking with 
                climate-adapted ingredients, the researchers demonstrate how communities might preserve food traditions while 
                adapting to environmental shifts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Approach */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Research Approach</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Leaf className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Climate Projections</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Analyzing climate models to predict ingredient availability in Highland Asia after 50 years
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Traditional Knowledge</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Working with elders and community members to understand traditional recipes and preparation methods
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Applied Research</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Hands-on cooking experiments substituting traditional ingredients with climate-resilient alternatives
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Community Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Gathering community responses to adapted dishes to understand cultural acceptability
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Findings */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Key Insights</h2>
            <div className="bg-gray-50 p-8 rounded-lg shadow-md">
              <div className="space-y-6">
                <div className="border-l-4 border-green-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Food as Cultural Adaptation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The project demonstrates how culinary practices can serve as a medium for exploring and negotiating 
                    climate adaptation while maintaining cultural identity and continuity.
                  </p>
                </div>

                <div className="border-l-4 border-green-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Ingredient Substitution Strategies</h3>
                  <p className="text-gray-700 leading-relaxed">
                    By experimenting with climate-resilient ingredients, the research identifies potential substitutes that 
                    preserve flavor profiles, nutritional value, and cultural significance of traditional dishes.
                  </p>
                </div>

                <div className="border-l-4 border-green-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Community Resilience</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The project highlights the importance of proactive adaptation strategies that engage communities in 
                    envisioning and preparing for climate futures while honoring culinary traditions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Publication */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Read the Full Research</h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Explore the complete findings, methodology, and implications of the Tasting Tomorrow project in our 
              published research article.
            </p>
            <a 
              href="https://journals.highlanderpress.org/index.php/Highlander/article/view/4/17"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg">
                Read Publication
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Explore More Research</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover other research projects at the Highland Institute addressing climate change, sustainability, 
            and community resilience.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/research">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                All Research Projects
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TastingTomorrowPage;
